<script setup lang="ts">
import {reactive, ref} from "vue";
import {ElNotification} from "element-plus";
import PageLabel from "components/common/PageLabel.vue";
import {TestService} from "@/api/api.ts";
import {AddQuestionForm, EditQuestionForm, TestQuestion} from "@/types";
import {getDateInfo, getTimeStr, testTypeList} from "@/utils/common.ts";
import AddTestQuestionDialog from "components/dialog/test/AddTestQuestionDialog.vue";
import BatchAddQuestionDialog from "components/dialog/test/BatchAddQuestionDialog.vue";
import EditTestQuestionDialog from "components/dialog/test/EditTestQuestionDialog.vue";
import router from "@/router";
import AllTestCateSelector from "components/test/AllTestCateSelector.vue";
import {useRoute} from "vue-router";

const route = useRoute()
const testCateSelector = ref<InstanceType<typeof AllTestCateSelector>>()
const addTestQuestionDialog = ref<InstanceType<typeof AddTestQuestionDialog>>()
const editTestQuestionDialog = ref<InstanceType<typeof EditTestQuestionDialog>>()
const batchAddQuestionDialog = ref<InstanceType<typeof BatchAddQuestionDialog>>()
const pagination = reactive({
  current: 1,
  pageSize: 10,
  total: 0
})
const loading = ref(false)
const id = ref("")
const content = ref("")
const reelId = ref()
const type = ref()
const range = ref<Date[]>([])
const questionList = ref<TestQuestion[]>()

const getQuestionList = async () => {
  try {
    loading.value = true
    const {start_time, end_time} = getDateInfo(range.value)
    const fCateId = testCateSelector.value?.getFCateId()
    const sCateId = testCateSelector.value?.getSCateId()
    const res = await TestService.getTestQuestionList({
      f_id: fCateId,
      s_id: sCateId,
      id: id.value,
      content: content.value,
      start_time: start_time,
      end_time: end_time,
      page: pagination.current,
      pageSize: pagination.pageSize,
      reel_id: reelId.value,
      type: type.value
    })
    if (res.data.code === 200) {
      questionList.value = res.data.data?.list
      pagination.current = res.data.data?.page || 1
      pagination.pageSize = res.data.data?.pageSize || 10
      pagination.total = res.data.data?.count || 0
    } else {
      ElNotification.error({
        title: '失败',
        message: res.data.msg,
        type: 'error',
      })
    }
  } catch (err) {
    console.warn(err)
  } finally {
    loading.value = false
  }
}

const onAdd = () => {
  addTestQuestionDialog.value?.show()
}

const onBatchAdd = () => {
  batchAddQuestionDialog.value?.show()
}

const onPageChange = (page: number) => {
  pagination.current = page
  getQuestionList()
}

const onSizeChange = (pageSize: number) => {
  pagination.pageSize = pageSize
  getQuestionList()
}

const toTestDetail = (id: number) => {
  const routeData = router.resolve({
    name: 'TestDetail',
    params: {
      id: id
    }
  })
  window.open(routeData.href, '_blank')
}

const onAddQuestion = async (form: AddQuestionForm) => {
  try {
    // @ts-ignore
    const res = await TestService.addEditTestQuestion(form)
    if (res.data.code === 200) {
      ElNotification.success({
        message: res.data.msg
      })
      addTestQuestionDialog.value?.hide()
      await getQuestionList()
    }
  } catch (err) {
    console.warn(err)
  }
}

const onEditQuestion = async (id: number) => {
  const res = await TestService.getTestQuestionInfo({id: id})
  if (res.data.code === 200) {
    if (res.data.data) {
      let answer = ""
      if (res.data.data.answer && res.data.data.answer.length > 0) {
        answer = res.data.data.answer.join(",")
      }
      const form: EditQuestionForm = {
        id: res.data.data.id,
        type: res.data.data.type,
        problem: res.data.data.problem,
        answer: answer,
        right_answer: res.data.data.right_answer,
        parsing: res.data.data.parsing,
        image: res.data.data.image,
        right_answer_image: res.data.data.right_answer_image,
        score: res.data.data.score.toString(),
        score_json: res.data.data.score_json,
      }
      editTestQuestionDialog.value?.show(form)
    }
  } else {
    ElNotification.error({
      message: res.data.msg
    })
  }
}

const onSubmitEditQuestion = async (form: EditQuestionForm) => {
  try {
    // @ts-ignore
    const res = await TestService.addEditTestQuestion(form)
    if (res.data.code === 200) {
      ElNotification.success({
        message: res.data.msg
      })
      editTestQuestionDialog.value?.hide()
      await getQuestionList()
    }
  } catch (err) {
    console.warn(err)
  }
}

const onSearch = () => {
  pagination.current = 1
  getQuestionList()
}

const onReset = () => {
  id.value = ""
  content.value = ""
  reelId.value = undefined
  type.value = undefined
  range.value = []
  testCateSelector.value?.reset()
  onSearch()
}

const onBatchAddSuccess = () => {
  onReset()
}

if (route.query.reel_id) {
  reelId.value = route.query.reel_id as string
}
getQuestionList()
</script>

<template>
  <div class="mx-3rem mt-2.25rem">
    <page-label>题目列表</page-label>
    <div class="p-1rem bg-white rounded-md mt-2rem shadow-md gap-1rem">
      <el-button type="primary" @click="onBatchAdd">批量上传</el-button>
      <el-button type="primary" @click="onAdd">添加题目</el-button>
      <div>
        <el-form inline class="mt-1rem">
          <all-test-cate-selector ref="testCateSelector" />
          <el-form-item label="题目类型">
            <el-select v-model="type" placeholder="请选择类型" class="my-selector">
              <el-option v-for="item in testTypeList" :label="item.label" :value="item.value"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="题目&emsp;ID">
            <el-input class="my-input" v-model="id" placeholder="请输入题目ID" @change="onSearch" clearable/>
          </el-form-item>
        </el-form>
        <el-form inline>
          <el-form-item label="题目搜索">
            <el-input class="my-input" v-model="content" placeholder="请输入关键字" @change="onSearch" clearable/>
          </el-form-item>
          <el-form-item label="套卷&emsp;ID">
            <el-input class="my-input" v-model="reelId" placeholder="请输入套卷ID" @change="onSearch" clearable/>
          </el-form-item>
          <el-form-item label="上传时间">
            <el-date-picker
                v-model="range"
                type="datetimerange"
                range-separator="至"
                start-placeholder="上传开始时间"
                end-placeholder="上传结束时间"
                style="width: 20rem"
                class="mr-1rem"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="onSearch">查询</el-button>
            <el-button type="danger" @click="onReset">重置</el-button>
          </el-form-item>
        </el-form>
      </div>
    </div>
    <div class="p-1rem bg-white rounded-md mt-1rem shadow-md" v-loading="loading">
      <el-table :data="questionList">
        <el-table-column prop="id" label="ID">
        </el-table-column>
        <el-table-column prop="problem" label="标题">
        </el-table-column>
        <el-table-column prop="type" label="题型">
          <template #default="scope">
            {{
              scope.row.type === 0 ? `组合题` : scope.row.type === 1 ? `单选` :
                  scope.row.type === 2 ? `多选` : scope.row.type === 3 ? `填空` :
                      scope.row.type === 4 ? `简答` : scope.row.type === 5 ? "竞赛" : ""
            }}
          </template>
        </el-table-column>
        <el-table-column prop="create_time" label="上传时间">
          <template #default="scope">
            {{ getTimeStr(scope.row.create_time * 1000) }}
          </template>
        </el-table-column>
        <el-table-column prop="f_cate_name" label="一级分类名称">
        </el-table-column>
        <el-table-column prop="s_cate_name" label="二级分类名称">
        </el-table-column>
        <el-table-column label="操作">
          <template #default="scope">
            <el-button type="primary" @click="toTestDetail(scope.row.id)">详情</el-button>
            <el-button type="primary" @click="onEditQuestion(scope.row.id)">编辑</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
          class="mt-1rem"
          background
          :page-sizes="[10, 20, 50, 100]"
          layout="prev, pager, next, jumper, ->, total, sizes"
          :current-page="pagination.current"
          :page-size="pagination.pageSize"
          :total="pagination.total"
          @current-change="onPageChange"
          @size-change="onSizeChange"/>
    </div>
  </div>
  <batch-add-question-dialog ref="batchAddQuestionDialog" @on-batch-add-success="onBatchAddSuccess"/>
  <add-test-question-dialog ref="addTestQuestionDialog" @on-add-question="onAddQuestion"/>
  <edit-test-question-dialog ref="editTestQuestionDialog" @on-edit-question="onSubmitEditQuestion"/>
</template>

<style scoped>
</style>
